import React, { useMemo, useSyncExternalStore } from "react";

import { Link } from "react-router-dom";

import { getDataStore } from "@/data/dataStore.js";

import { canViewDocument, useAuth } from "@/context/AuthContext.jsx";

import { DataTable } from "@/components/DataTable.jsx";

import { DocumentVisibilityBadge } from "@/components/DocumentVisibilityBadge.jsx";

import { EntityStatusBadge } from "@/components/DocumentVisibilityBadge.jsx";

import { PageSection } from "@/components/AppLayout.jsx";

import { EmptyState } from "@/coquille-bridge";

import { getMartialTitles } from "@/content/martialTitlesI18n.js";

import { getProtocoleSections } from "@/content/protocoleSectionsI18n.js";

import { usePublicCopy } from "@/i18n/karatePublicI18n.js";

import { useKarateUi } from "@/hooks/useKarateUi.js";

import { getDocumentVisibilityWarningLocalized } from "@/i18n/phase1UiHelpers.js";



function useDataStore() {

  return useSyncExternalStore(

    (cb) => {

      window.addEventListener("karate-data-updated", cb);

      return () => window.removeEventListener("karate-data-updated", cb);

    },

    () => getDataStore(),

    () => getDataStore(),

  );

}



export function DocumentsPublicPage() {

  const { role } = useAuth();

  const { copy, locale } = usePublicCopy();

  const doc = copy.documents;

  const d = copy.directory;

  const { localizeDemo, visibilityLabel } = useKarateUi();

  const { documents } = useDataStore();

  const visible = useMemo(

    () => documents.filter((x) => canViewDocument(x.visibilite, role)),

    [documents, role],

  );

  const pendingValidation = useMemo(

    () => documents.filter((x) => x.visibilite === "a_valider"),

    [documents],

  );



  const visibilityOptions = useMemo(

    () =>

      ["public", "membres", "admin", "a_valider"].map((v) => ({

        value: v,

        label: visibilityLabel(v),

      })),

    [visibilityLabel],

  );



  const columns = useMemo(

    () => [

      { key: "titre", label: d.columns.title, render: (row) => localizeDemo(row.titre) },

      { key: "categorie", label: d.columns.category, render: (row) => localizeDemo(row.categorie) },

      {

        key: "visibilite",

        label: d.columns.visibility,

        render: (row) => <DocumentVisibilityBadge visibilite={row.visibilite} />,

      },

      { key: "reference", label: d.columns.reference },

      {

        key: "statut",

        label: d.columns.status,

        render: (row) => <EntityStatusBadge statut={row.statut} />,

      },

    ],

    [d, localizeDemo],

  );



  return (

    <PageSection title={doc.title} subtitle={doc.subtitle} testId="page-documents">

      <div className="warning-box" data-testid="doc-sensitive-warning">

        <strong>{locale === "en" ? "Important:" : "Important :"}</strong> {doc.warning}

      </div>



      <div className="info-box" data-testid="doc-visibility-legend">

        <strong>{locale === "en" ? "Legend:" : "Légende :"}</strong> {doc.legend}

      </div>



      {pendingValidation.length > 0 ? (

        <section className="pending-docs-section" data-testid="docs-pending-section">

          <h2 className="section-h2">{doc.pendingTitle}</h2>

          <ul className="pending-docs-list">

            {pendingValidation.map((x) => (

              <li key={x.id}>

                <strong>{localizeDemo(x.titre)}</strong> — {localizeDemo(x.categorie)}

                {getDocumentVisibilityWarningLocalized(x.visibilite, x.titre, locale) ? (

                  <span className="pending-docs-list__warn">

                    {" "}

                    · {getDocumentVisibilityWarningLocalized(x.visibilite, x.titre, locale)}

                  </span>

                ) : null}

              </li>

            ))}

          </ul>

          {role === "hanshi" || role === "admin" ? (

            <Link to="/admin/documents" className="btn-secondary btn-sm">

              {doc.classifyAdmin}

            </Link>

          ) : null}

        </section>

      ) : null}



      {visible.length === 0 ? (

        <EmptyState
          message={role === "guest" || !role ? doc.emptyGuest : doc.emptyMember}
          testId="documents-empty"
        />

      ) : (

        <DataTable

          rows={visible}

          columns={columns}

          searchFields={["titre", "categorie", "reference"]}

          emptyMessage={doc.emptyMember}

          emptyFilteredMessage={doc.emptyFiltered}

          filters={[

            {

              field: "visibilite",

              label: d.columns.visibility,

              options: visibilityOptions,

            },

          ]}

          testId="documents-table"

        />

      )}

    </PageSection>

  );

}



export function ProtocolsPage() {

  const { role } = useAuth();

  const { copy, locale } = usePublicCopy();

  const prot = copy.protocols;

  const sections = getProtocoleSections(locale).filter((s) => canViewDocument(s.visibilite, role));

  const martialTitles = getMartialTitles(locale);



  return (

    <PageSection title={prot.title} subtitle={prot.subtitle} testId="page-protocoles">

      <div className="warning-box" data-testid="protocoles-warning">

        {prot.warning}

      </div>



      <section className="titles-explainer" data-testid="protocoles-titles">

        <h2 className="section-h2">{prot.structureTitle}</h2>

        <div className="titles-grid titles-grid--compact">

          {martialTitles.slice(0, 6).map((t) => (

            <article key={t.titre} className="title-card title-card--compact">

              <h3>{t.titre}</h3>

              <p>{t.description}</p>

            </article>

          ))}

        </div>

      </section>



      {sections.length === 0 ? (

        <EmptyState
          message={role === "guest" || !role ? prot.emptyGuest : prot.emptyMember}
          testId="protocoles-empty"
        />

      ) : (

        <div className="protocol-list">

          {sections.map((s) => (

            <article key={s.id} className="protocol-card" data-testid={`protocole-${s.id}`}>

              <header className="protocol-card__header">

                <h2>{s.titre}</h2>

                <DocumentVisibilityBadge visibilite={s.visibilite} />

              </header>

              <p>{s.contenu}</p>

            </article>

          ))}

        </div>

      )}

    </PageSection>

  );

}


